import { action, computed, observable, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { ChangeEvent } from 'react';
import { validateIsNumber } from 'src/api/utils/formValidators';
import { ResultError } from 'src_common/common/mobx-utils/Form';
import { MarketBaseSettingsPropsType } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/components/marketBaseSettings/MarketBaseSettings';

export class MarketBaseSettingsState {
    @observable private inPlayDelay: string | null = null;
    @observable private eachWayStakeReduction: string | null = null;
    @observable public inPlayError: string | null = null;
    @observable public eachWayStakeReductionError: string | null = null;

    public constructor(
        private readonly appState: AppState,
        private readonly props: MarketBaseSettingsPropsType
    ) {
        makeAutoObservable(this);
    }

    @computed public get getInPlayDelay(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        return this.inPlayDelay === null ? marketModel.inPlayDelay.toString() : this.inPlayDelay;
    }

    @action public onChangeInPlayDelay = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        if (validateIsNumber(value) instanceof ResultError) {
            this.inPlayError = 'Only digits are allowed.';
        } else {
            this.inPlayError = null;
        }
        this.inPlayDelay = event.target.value;
    };

    @action public onSaveInPlayDelay = async (): Promise<void> => {
        if (this.inPlayError !== null) {
            return;
        }
        const delayPayload =
            this.inPlayDelay === null ? this.getInPlayDelay : this.inPlayDelay.trim() === '' ? '0' : this.inPlayDelay;
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            inPlayDelay: parseInt(delayPayload, 10),
        });
    };

    @computed public get getEachWayStakeReduction(): string {
        const marketModel = this.props.marketId.getModel();
        if (marketModel === null) {
            return '';
        }

        return this.eachWayStakeReduction === null
            ? marketModel.eachWayStakeReduction.toString()
            : this.eachWayStakeReduction;
    }

    @action public onChangeEachWayStake = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        if (validateIsNumber(value) instanceof ResultError) {
            this.eachWayStakeReductionError = 'Only digits are allowed.';
        } else {
            this.eachWayStakeReductionError = null;
        }

        this.eachWayStakeReduction = event.target.value;
    };

    @action public onSaveEachWayStake = async (): Promise<void> => {
        if (this.eachWayStakeReductionError !== null) {
            return;
        }
        const eachWayPayload =
            this.eachWayStakeReduction === null || this.eachWayStakeReduction.trim() === ''
                ? '0'
                : this.eachWayStakeReduction;
        await this.appState.eventStateActions.saveMarketChanges(
            this.props.marketId,
            { eachWayStakeReduction: eachWayPayload },
            true
        );
    };

    @action public onChangeStartingPrice = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { sp: event.target.checked });
    };

    @action public onChangeStartingPriceOnly = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { spOnly: event.target.checked });
    };

    @action public onChangeBestOdds = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, { bp: event.target.checked });
    };

    @action public onChangeExtraPlacesIndicator = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        const marketModel = this.props.marketId.getModel();

        if (marketModel === null) {
            console.error('onChangeExtraPlacesIndicator: market model is unavailable');
            return;
        }

        await this.appState.sdkStaff.updateEventTag(
            marketModel.eventId2,
            'extra-places-indicator',
            event.target.checked ? 'true' : 'false'
        );
        await this.appState.sdkStaff.platformTags.refresh();
    };

    @action public onChangeBlockSettlement = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            blockSettlement: event.target.checked,
        });
    };

    @action public onChangeBetReferral = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(
            this.props.marketId,
            { betReferralEnabled: event.target.checked },
            true
        );
    };

    @action public onChangeSinglesOnly = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            singlesOnly: event.target.checked,
        });
    };

    @action public onChangeTraderInPlay = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        await this.appState.eventStateActions.saveMarketChanges(this.props.marketId, {
            tradedInPlay: event.target.checked,
        });
    };
}
